import type { ScanTypes } from '@blackfisk/domain/barcode/types.js'

import { computed, shallowRef, watch } from 'vue'

import { useScanner } from './useScanner.js'

export type Termination = boolean | 'match'
export interface ListenerOptions {
  priority: number
  termination?: Termination
  terminationWhitelist?: string[]
  inputs?: boolean
}
export interface Listener {
  priority: number
  termination: Termination
  terminationWhitelist: string[]
  bindings: Bindings
}
export type Bindings = Partial<
  Record<keyof ScanTypes, (scanResult: ScanTypes[keyof ScanTypes]) => boolean | void>
>
const listeners = shallowRef<Listener[]>([])
const prioritizedListeners = computed(() => {
  return [...listeners.value].toSorted((firstListener: Listener, secondListener: Listener) => {
    if (firstListener.priority < secondListener.priority) {
      return 1
    } else if (firstListener.priority > secondListener.priority) {
      return -1
    }
    return 0
  })
})
const listen = (bindings: Bindings, options: ListenerOptions): (() => void) => {
  const listener = {
    priority: options.priority,
    termination: options?.termination ?? true,
    terminationWhitelist: options?.terminationWhitelist ?? [],
    bindings,
  }
  addListener(listener)
  const unlisten = (): void => {
    removeListener(listener)
  }
  return unlisten
}
const addListener = (listener: Listener): Listener => {
  listeners.value = [...listeners.value, listener]
  return listener
}
const removeListener = (listenerToRemove: Listener): void => {
  listeners.value = listeners.value.filter((listener: Listener) => {
    return listener !== listenerToRemove
  })
}

export const useScanListener = () => {
  const register = (): void => {
    const { latestScan } = useScanner()
    watch(latestScan, () => {
      if (latestScan.value) {
        for (const listener of prioritizedListeners.value) {
          let result: boolean | void = false
          if (listener.bindings[latestScan.value.type]) {
            result = listener.bindings[latestScan.value.type]?.(latestScan.value)
          }
          if (result === false) {
            continue
          } else if (result === true) {
            return
          }
          if (listener.termination === 'match') {
            return
          }
          if (
            listener.termination === true &&
            !listener.terminationWhitelist.includes(latestScan.value.type)
          ) {
            return
          }
        }
      }
    })
  }
  return {
    registerListeners: register,
    listeners,
    listen,
  }
}
